import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import type { Reflection } from "../types";

interface UseCheckInDataReturn {
	reflections: Reflection[];
	totalCheckInsCount: number | null;
	loading: boolean;
	refetchReflections: () => Promise<void>;
}

export function useCheckInData(
	userId: string | undefined,
): UseCheckInDataReturn {
	const [reflections, setReflections] = useState<Reflection[]>([]);
	const [totalCheckInsCount, setTotalCheckInsCount] = useState<number | null>(
		null,
	);
	const [loading, setLoading] = useState(true);

	const fetchUserReflections = async () => {
		if (!userId) return;

		try {
			const { data, error } = await supabase
				.from("reflections")
				.select("id, title, description")
				.eq("user_id", userId)
				.eq("archived", false)
				.eq("is_draft", false)
				.order("created_at", { ascending: false })
				.limit(3);

			if (error) {
				console.error("Error fetching reflections:", error);
				return;
			}

			if (data) {
				const reflectionsWithSignals = await Promise.all(
					data.map(async (reflection) => {
						const { data: signalsData, error: signalsError } = await supabase
							.from("signals")
							.select("id, title, description")
							.eq("reflection_id", reflection.id);

						if (signalsError) {
							console.error(
								"Error fetching signals for reflection:",
								signalsError,
							);
						}

						return {
							...reflection,
							signals: signalsData || [],
						};
					}),
				);

				setReflections(reflectionsWithSignals);
			}
		} catch (error) {
			console.error("Error fetching reflections:", error);
		}
	};

	const fetchTotalCheckInsCount = async () => {
		if (!userId) return;

		try {
			const { count, error } = await supabase
				.from("daily_checkins")
				.select("*", { count: "exact", head: true })
				.eq("user_id", userId);

			if (error) {
				console.error("Error fetching total check-ins count:", error);
				return;
			}

			setTotalCheckInsCount(count || 0);
		} catch (error) {
			console.error("Error fetching total check-ins count:", error);
		}
	};

	useEffect(() => {
		if (userId) {
			Promise.all([fetchUserReflections(), fetchTotalCheckInsCount()]).finally(
				() => {
					setLoading(false);
				},
			);
		}
	}, [userId]);

	return {
		reflections,
		totalCheckInsCount,
		loading,
		refetchReflections: fetchUserReflections,
	};
}
